import React, { useEffect, useState } from "react";
import { css, styled } from "styled-components";
import { theme } from "../styles/theme";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";


interface VideoProps {
  index: number;
  status: "PLAYED" | "PLAYING" | "TOPLAY";
  progress: number;
  duration: number;
}

interface PlayerBarProps {
  videos: VideoProps[];
  currentIndex: number;
  hideNavs?: boolean;
  barColor?: string;
  onSelectVideo?: (index: number) => void;
}


const BarContainer = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  gap: 24px;
`;

const Bar = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const ProgressLine = styled.div<{color?: string}>`
  position: absolute;
  height: 6px;
  background-color: ${props=>props.color || "#e0e0e0"};
  width: 100%;
  z-index: 1;
  border-radius: 4px;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const CompletedLine = styled.div<{ width: number }>`
  position: absolute;
  height: 6px;
  background-color: #4513AA;
  width: ${(props) => props.width}%;
  z-index: 2;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const Counter = styled.div<{ status: "PLAYED" | "PLAYING" | "TOPLAY" }>`
  width: 25px;
  height: 25px;
  border-radius: 100px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 3;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.status === 'PLAYED' && css`
    border: 1px solid ${theme.colors.secondary};
    background: #FFFFFF;
    color: ${theme.colors.secondary};
  `};
  
  ${props => props.status === 'PLAYING' && css`
    border: 1px solid ${theme.colors.secondary};
    background: ${theme.colors.secondary};
    color: ${theme.colors.white};
    transform: scale(1.1);
  `};
  
  ${props => props.status === 'TOPLAY' && css`
    border: 1px solid #C7C4D3;
    background: #FFFFFF;
    color: #000000;
  `}
  
  &>span {
    font-family: ${theme.fonts.primary};
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.xs};
    line-height: 100%;
    text-align: center;
  }
`;


const CounterContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  z-index: 3;
`;

const CounterWrapper = styled.div<{ position: number }>`
  position: absolute;
  top: -13px;
  left: ${props => props.position}%;
  transform: translateX(-50%);
`;

const PlayerBar: React.FC<PlayerBarProps> = ({ videos, currentIndex, onSelectVideo, hideNavs=true, barColor='#e0e0e0' }) => {
  const [positions, setPositions] = useState<number[]>([]);

  const calculateTotalDuration = () => {
    return videos.reduce((total, video) => total + (video.duration || 0), 0);
  };
  
  
  const calculateCounterPosition = (index: number) => {
    if (videos.length === 0) return 0;
    if (index === 0) return 0;
    
    const totalDuration = calculateTotalDuration();
    if (totalDuration === 0) {
      return (index / videos.length) * 100;
    }
    
    
    const durationSum = videos
      .slice(0, index)
      .reduce((sum, video) => sum + (video.duration || 0), 0);
    
    
    return (durationSum / totalDuration) * 100;
  };
  
  
  const calculateOverallProgress = () => {
    if (!videos || videos.length < 1) return 0;
    
    const totalDuration = calculateTotalDuration();
    if (totalDuration === 0) return 0;

   
    const completedDuration = videos
      .slice(0, currentIndex)
      .reduce((sum, video) => sum + (video.duration || 0), 0);
    
    // Get counter positions
    const currentCounterPosition = calculateCounterPosition(currentIndex);
    const nextCounterPosition = currentIndex < videos.length - 1 
      ? calculateCounterPosition(currentIndex + 1) 
      : 100;

    // Allocated width for this video's progress
    const allocatedWidth = nextCounterPosition - currentCounterPosition;

    const currentVideoDuration = videos[currentIndex].duration || 1;

    // Ensure the progress bar stays within its allocated width
    const normalizedProgress = (videos[currentIndex].progress / 100) * (currentVideoDuration / totalDuration) * 100;

    return Math.min(currentCounterPosition + normalizedProgress, nextCounterPosition);
  };

  const calculateSegmentProgress = () => {
    if (!videos || videos.length === 0) return 0;

    const totalDuration = calculateTotalDuration();
    const video = videos[currentIndex];

    if (!video || video.duration === 0) return 0;
    
    const currentCounterPosition = calculateCounterPosition(currentIndex);
    const nextCounterPosition = currentIndex < videos.length - 1
        ? calculateCounterPosition(currentIndex + 1)
        : 100;
    
    const allocatedWidth = nextCounterPosition - currentCounterPosition;
    
    const progressStep = allocatedWidth / video.duration;
    let segmentProgress = video.progress * progressStep;
    if(video.progress>video.duration){
      segmentProgress=0
    }
    return Math.min(currentCounterPosition + segmentProgress, nextCounterPosition);
  };


  const handleCounterClick = (index: number) => {
    if (onSelectVideo) {
      onSelectVideo(index);
    }
  };

  // useEffect(()=>{
  //   if(videos.length>0){
  //     videos.map((video, idx) => {
  //       const position = calculateCounterPosition(idx);
  //       setPositions(prev => [...prev, position]);
  //     })
  //   }
  // }, [videos])

  return (
    <BarContainer>
      {!hideNavs && <FiArrowLeftCircle size={32} />}
      <Bar>
        <ProgressLine color={barColor}>
          <CompletedLine width={calculateSegmentProgress()} />
        </ProgressLine>
        
        <CounterContainer>
          {videos.map((video, idx) => {
            const position = calculateCounterPosition(idx);
            
            return (
              <CounterWrapper key={idx} position={position}>
                <Counter
                  status={video.status}
                  onClick={() => handleCounterClick(idx)}
                >
                  <span>{idx + 1}</span>
                </Counter>
              </CounterWrapper>
            );
          })}
        </CounterContainer>  
      </Bar>
      
      {!hideNavs && <FiArrowRightCircle size={32} />}
    </BarContainer>
  );
};

export default PlayerBar;